import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Lightning, Fingerprint, ArrowRight, Eye, Target, Warning } from '@phosphor-icons/react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SYMBOL_MAP = {
  'BLITZ': Lightning,
  'KOMPASS': Target,
  'FLAMME': Lightning,
  'AUGE': Eye,
};

const InstinctProfile = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | generating | done | error
  const [profile, setProfile] = useState(null);
  const [revealStep, setRevealStep] = useState(0);

  useEffect(() => {
    const trigger = async () => {
      try {
        await axios.post(`${API}/user/instinct-profile`, { session_id: sessionId });
      } catch (err) {
        console.error('Trigger error:', err);
      }
    };
    trigger();
  }, [sessionId]);

  useEffect(() => {
    const poll = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/user/instinct-profile/${sessionId}`);
        const data = res.data;
        if (data.status === 'done') {
          setProfile(data.profile);
          setStatus('done');
          clearInterval(poll);
          // Start reveal sequence
          setTimeout(() => setRevealStep(1), 400);
          setTimeout(() => setRevealStep(2), 1200);
          setTimeout(() => setRevealStep(3), 2000);
          setTimeout(() => setRevealStep(4), 2800);
          setTimeout(() => setRevealStep(5), 3600);
        } else if (data.status === 'error') {
          setStatus('error');
          clearInterval(poll);
        } else {
          setStatus('generating');
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    }, 2000);

    return () => clearInterval(poll);
  }, [sessionId]);

  if (status === 'loading' || status === 'generating') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ background: 'var(--bg-void)' }}>
        <div className="noise-overlay" />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center max-w-md px-4"
        >
          <motion.div
            animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Lightning size={64} weight="fill" style={{ color: 'var(--accent-gold)' }} className="mx-auto mb-6" />
          </motion.div>

          <h2 className="text-2xl font-black mb-4" style={{ fontFamily: 'var(--font-heading)' }} data-testid="generating-title">
            DEIN INSTINCT-PROFIL WIRD ERSTELLT
          </h2>

          {/* Scanning lines animation */}
          <div className="space-y-2 mb-6">
            {['Rabbit Holes analysieren...', 'Kindheits-Muster erkennen...', 'Frustrations-Inversion berechnen...', 'Verborgene Staerken entschluesseln...'].map((text, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: [0.3, 1, 0.3], x: 0 }}
                transition={{ delay: i * 1.5, duration: 2, repeat: Infinity }}
                className="text-sm"
                style={{ color: 'var(--accent-gold)' }}
              >
                {text}
              </motion.div>
            ))}
          </div>

          <div className="loading-bar w-64 mx-auto">
            <div className="loading-bar-fill" />
          </div>
        </motion.div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ background: 'var(--bg-void)' }}>
        <div className="noise-overlay" />
        <Warning size={48} style={{ color: 'var(--accent-cyan)' }} className="mb-4" />
        <p className="text-mono mb-4" style={{ color: 'var(--accent-cyan)' }}>Instinct-Profil konnte nicht erstellt werden.</p>
        <div className="flex gap-3">
          <button
            onClick={() => {
              setStatus('loading');
              axios.post(`${API}/user/instinct-profile`, { session_id: sessionId }).catch(() => {});
            }}
            className="text-sm px-6 py-3"
            style={{ border: '1px solid var(--accent-cyan)', color: 'var(--accent-cyan)', background: 'transparent' }}
            data-testid="retry-instinct-btn"
          >
            ERNEUT VERSUCHEN
          </button>
          <button
            onClick={() => navigate(`/dashboard/${sessionId}`)}
            className="text-sm px-6 py-3"
            style={{ border: '1px solid var(--border-default)', color: 'var(--text-primary)', background: 'transparent' }}
            data-testid="continue-dashboard-btn"
          >
            Zurueck ZUM DASHBOARD
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative" style={{ background: 'var(--bg-void)' }}>
      <div className="noise-overlay" />

      {/* Accent stripe */}
      <div className="fixed top-0 left-0 w-full h-1 z-50" style={{ background: 'linear-gradient(90deg, var(--accent-gold), #FBBF24, #EF4444, #22C55E)' }} />

      <div className="container mx-auto px-4 py-16 max-w-2xl min-h-screen flex flex-col justify-center">

        {/* Archetype Name — First Reveal */}
        <AnimatePresence>
          {revealStep >= 1 && profile && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className="text-center mb-12"
            >
              <motion.div
                initial={{ y: -20 }}
                animate={{ y: 0 }}
                className="inline-block px-4 py-1 mb-4 text-xs font-bold tracking-widest"
                style={{ background: 'var(--accent-gold)', color: 'var(--text-primary)' }}
                data-testid="instinct-badge"
              >
                DEIN INSTINCT-PROFIL
              </motion.div>

              <h1
                className="text-4xl md:text-5xl font-black leading-tight mb-2"
                style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-primary)' }}
                data-testid="archetype-name"
              >
                {profile.archetype_name}
              </h1>

              <p className="text-sm" style={{ color: 'var(--accent-gold)' }}>
                [{profile.archetype_emoji}]
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Profile Cards */}
        <div className="space-y-4">
          {/* Core Drive */}
          <AnimatePresence>
            {revealStep >= 2 && profile && (
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                className="p-5"
                style={{ background: 'var(--bg-surface)', borderLeft: '3px solid var(--accent-gold)' }}
                data-testid="core-drive-card"
              >
                <span className="text-overline block mb-2" style={{ color: 'var(--accent-gold)' }}>ANTRIEB</span>
                <p className="text-base" style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                  {profile.core_drive}
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Hidden Superpower */}
          <AnimatePresence>
            {revealStep >= 3 && profile && (
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                className="p-5"
                style={{ background: 'var(--bg-surface)', borderLeft: '3px solid #22C55E' }}
                data-testid="superpower-card"
              >
                <span className="text-overline block mb-2" style={{ color: '#22C55E' }}>VERBORGENE SUPERKRAFT</span>
                <p className="text-base" style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                  {profile.hidden_superpower}
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Blind Spot */}
          <AnimatePresence>
            {revealStep >= 4 && profile && (
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                className="p-5"
                style={{ background: 'var(--bg-surface)', borderLeft: '3px solid #EF4444' }}
                data-testid="blind-spot-card"
              >
                <span className="text-overline block mb-2" style={{ color: '#EF4444' }}>BLINDER FLECK</span>
                <p className="text-base" style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                  {profile.blind_spot}
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* First Truth */}
          <AnimatePresence>
            {revealStep >= 5 && profile && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div
                  className="p-6 mt-4"
                  style={{ background: 'var(--bg-surface)', border: '2px solid #FBBF24' }}
                  data-testid="first-truth-card"
                >
                  <span className="text-overline block mb-3" style={{ color: '#FBBF24' }}>DIE ERSTE WAHRHEIT</span>
                  <p className="text-base leading-relaxed" style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                    {profile.first_truth}
                  </p>
                </div>

                {/* CTA */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="mt-8 text-center"
                >
                  <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                    Das war nur der Anfang. Starte die Deep-Analyse.
                  </p>
                  <button
                    onClick={() => navigate(`/dashboard/${sessionId}`)}
                    className="inline-flex items-center gap-3 px-8 py-4 font-bold text-mono transition-all hover:scale-105"
                    style={{ background: 'var(--accent-cyan)', color: '#fff', border: 'none', borderRadius: '9999px' }}
                    data-testid="continue-btn"
                  >
                    Weiter ZUR DEEP-ANALYSE <ArrowRight size={20} weight="bold" />
                  </button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default InstinctProfile;
